import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';
import { Spinner } from './Spinner';

type ButtonVariant = 'primary' | 'inverse' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-input font-semibold ' +
  'transition-colors duration-150 focus-visible:outline-none disabled:cursor-not-allowed ' +
  'disabled:opacity-60';

const variants: Record<ButtonVariant, string> = {
  // Brand variants ported from the website button system.
  primary: 'bg-primary text-white hover:bg-[#004876] active:bg-[#003a60]',
  inverse: 'bg-inverse text-white hover:bg-[#013454] active:bg-[#001d33]',
  secondary: 'bg-secondary text-white hover:bg-[#697680]',
  outline:
    'border border-muted-300 bg-white text-ink hover:bg-muted-100 active:bg-muted-200',
  ghost: 'bg-transparent text-primary hover:bg-primary-light',
  danger: 'bg-danger text-white hover:bg-[#e05600] active:bg-[#c44d00]',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

/**
 * Primary action button for the design system. Mirrors the Gral website button
 * variants (`btn-primary`, `btn-inverse`, …) with a built-in loading state.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, block = false, className, children, disabled, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...props}
    >
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
});
